import React, { useRef, useState, useEffect } from "react";
import { Music, Loader2, Upload, RefreshCw, Save, Library, Check, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const THEMES = [
  ["auto", "✨ Auto"], ["retro", "🕹️ Retro"], ["fantasy", "🏰 Fantasy"],
  ["scifi", "🚀 Sci-Fi"], ["cute", "🍬 Cute"], ["action", "💥 Action"],
];

// Owner-only soundtrack control: recompose by theme (AI), upload a custom track, or
// save/apply soundtracks from a reusable Music Library. Players mute via the in-game 🔊 button.
export default function GameMusic({ gameId, game, user, onConfig }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [lib, setLib] = useState([]);
  const fileRef = useRef(null);
  const isOwner = user && game && (user.id === game.owner_id || ["admin", "superadmin"].includes(user.role));

  const loadLib = () => api.get("/audio/soundtracks").then((r) => setLib(r.data || [])).catch(() => {});
  useEffect(() => { if (open) loadLib(); }, [open]);

  if (!isOwner) return null;

  const regenerate = async (theme) => {
    setBusy(theme);
    try {
      const { data } = await api.post("/audio/pack", { game_id: gameId, theme });
      toast.success("New soundtrack composed! Restart to hear it.");
      onConfig?.(data.config);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't compose that soundtrack.");
    } finally { setBusy(""); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Track too large (max 8MB)."); return; }
    setBusy("upload");
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f);
      });
      const { data } = await api.post(`/audio/game-bgm/${gameId}`, { audio: dataUrl, name: f.name.slice(0, 60) });
      toast.success("Your track is set! Restart to hear it.");
      onConfig?.(data.config);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't set that track.");
    } finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveToLibrary = async () => {
    setBusy("save");
    try {
      await api.post("/audio/soundtracks/save", { game_id: gameId, name: game.title });
      toast.success("Soundtrack saved to your library!");
      loadLib();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the soundtrack.");
    } finally { setBusy(""); }
  };

  const applyEntry = async (entry) => {
    setBusy(`apply-${entry.id}`);
    try {
      const { data } = await api.post(`/audio/soundtracks/${entry.id}/apply`, { game_id: gameId });
      toast.success(`"${entry.name}" applied! Restart to hear it.`);
      onConfig?.(data.config);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't apply that soundtrack.");
    } finally { setBusy(""); }
  };

  const deleteEntry = async (entry) => {
    setBusy(`del-${entry.id}`);
    try {
      await api.delete(`/audio/soundtracks/${entry.id}`);
      setLib((l) => l.filter((x) => x.id !== entry.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete that.");
    } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="game-music-btn"><Music className="w-4 h-4" /> Music</Button>
      </DialogTrigger>
      <DialogContent data-testid="game-music-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Music className="w-5 h-5 text-fuchsia-500" /> Game soundtrack</DialogTitle>
          <DialogDescription>Recompose the music, use your own track, or reuse a saved soundtrack. Players mute anytime with the 🔊 button in-game.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><RefreshCw className="w-4 h-4" /> Recompose with AI</div>
            <div className="flex flex-wrap gap-2">
              {THEMES.map(([v, label]) => (
                <button key={v} type="button" disabled={!!busy} onClick={() => regenerate(v)} data-testid={`game-music-theme-${v}`}
                  className="rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all border-[hsl(var(--border))] hover:border-fuchsia-500/60 disabled:opacity-50">
                  {busy === v ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-[hsl(var(--border))] flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="audio/*" onChange={upload} className="hidden" data-testid="game-music-file" />
            <Button variant="outline" size="sm" className="rounded-full" disabled={!!busy} onClick={() => fileRef.current?.click()} data-testid="game-music-upload-btn">
              {busy === "upload" ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload your own</>}
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={!!busy} onClick={saveToLibrary} data-testid="game-music-save-btn">
              {busy === "save" ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save to Library</>}
            </Button>
          </div>

          <div className="pt-3 border-t border-[hsl(var(--border))]">
            <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><Library className="w-4 h-4" /> My Library</div>
            {lib.length === 0 ? (
              <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="game-music-lib-empty">No saved soundtracks yet. Compose or upload one, then tap "Save to Library" to reuse it on any game.</p>
            ) : (
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1" data-testid="game-music-lib">
                {lib.map((e) => (
                  <div key={e.id} data-testid={`game-music-lib-${e.id}`} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] p-2">
                    <div className="w-8 h-8 rounded-lg grid place-items-center bg-fuchsia-500/10 shrink-0"><Music className="w-4 h-4 text-fuchsia-500" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{e.name}</div>
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{e.track_count} track{e.track_count === 1 ? "" : "s"}{e.bgm_name ? ` · ${e.bgm_name}` : ""}</div>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full h-8 px-3" disabled={!!busy} onClick={() => applyEntry(e)} data-testid={`game-music-apply-${e.id}`}>
                      {busy === `apply-${e.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Apply</>}
                    </Button>
                    <button type="button" disabled={!!busy} onClick={() => deleteEntry(e)} data-testid={`game-music-del-${e.id}`} className="text-[hsl(var(--muted-foreground))] hover:text-red-500 disabled:opacity-40 shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
